<template lang="pug">
el-form(
  ref="refForm"
  :rules="rules"
  :model="item"
  status-icon
  label-position="top"
).c-segment-detail-form
  app-main-content-layout(
    title='Segment'
    back-to='/manage-content/directory/segments'
    v-loading="loading"
  )
    template(#actions)
      el-button(type="danger" plain @click="removeSegment").has-fai.min-w-110
        fai(type="fal" icon="trash").size-18
        span Delete

      el-button(type="primary" @click="validateForm" v-loading="loading").has-fai.min-w-110
        fai(type="fal" icon="save").size-18
        span Save

    template(#col-left)
      .c-content-section
        el-form-item(label="Segment Title" prop="segment")
          el-input(v-model="item.segment")

        el-form-item(label="Details" prop="description").mb-0
          el-input(v-model="item.description" type="textarea" :rows="6")

    template(#col-right)
      .c-content-section
        h5.mb-3 Cover Image
        el-upload(
          :show-file-list="false"
          :on-success="handleSuccess"
          :on-error="handleError"
          :before-upload="beforeUpload"
          :http-request="handleSegmentCoverUpload"
          v-loading="uploading"
          action=""
        ).img-uploader.pos-r.mb-4
          template(v-if="item.cover")
            img(:src="item.cover").img-fluid.uploaded-image
            .pin.pin-bx.d-flex.p-2
              el-button(type="primary").ms-auto Change
          template(v-else)
            .upload-empty-state
              img(src="@/assets/icons/file-image-outline.svg").img-uploader-icon
              p.upload-picture-text Drop image here or
              el-button(type="primary" size="small").fake-upload-button Browse
</template>

<style lang="scss" scoped>
// Color Variables - Match OrgEdit.vue
$color-teal-accent: #01beb0;
$gray-50: #f9fafb;
$gray-100: #f3f4f6;
$gray-200: #e5e7eb;
$gray-300: #d1d5db;
$gray-400: #9ca3af;
$gray-500: #6b7280;
$gray-600: #4b5563;
$gray-700: #374151;
$gray-900: #111827;
$color-purple-text: #4349dd;

// Main Form Container
.c-segment-detail-form {
  padding: 2rem;
  
  :deep(.grid-2-cols) {
    gap: 1.5rem;
  }
  
  :deep(.col-has-child) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}

// White Content Sections
.c-content-section {
  background: white;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: none;
  padding: 1.5rem;
  overflow: hidden;
}

// Form Styling
:deep(.el-form-item) {
  margin-bottom: 1.5rem;

  .el-form-item__label {
    color: $gray-700;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.5;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .el-input__wrapper {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 0 12px;
    height: 40px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover:not(.is-disabled) {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &.is-focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &.is-disabled {
      border: 1.5px solid $gray-200;
      background: #F0F0F3;
    }
  }

  .el-input__inner {
    height: 100%;
    font-size: 0.875rem;
    color: $gray-700;
    background: transparent;
    font-weight: 500;

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }

  .el-textarea__inner {
    border-radius: 10px;
    border: 1.5px solid $gray-200;
    background: #F0F0F3;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
    padding: 12px;
    color: $gray-700;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    min-height: 100px;

    &:hover {
      border-color: #c0c4cc;
      box-shadow: none;
    }

    &:focus {
      border-color: #01beb0;
      box-shadow: none;
    }

    &::placeholder {
      color: $gray-400;
      font-weight: 400;
    }
  }
}

// Button Styling
:deep(.el-button) {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  height: 36px;
  padding: 0 20px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-width: 1px;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03);
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.5rem;

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .fai,
  svg {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    margin-right: 0.5rem;
  }

  &.min-w-110 {
    min-width: 110px;
  }

  &.el-button--primary {
    background-color: $color-purple-text;
    border-color: $color-purple-text;
    color: white;

    &:hover,
    &:focus {
      background-color: color.adjust($color-purple-text, $lightness: -8%);
      border-color: color.adjust($color-purple-text, $lightness: -8%);
      box-shadow: 0 2px 4px 0 rgba(67, 73, 221, 0.2);
      transform: translateY(-1px);
    }

    &:active {
      background-color: color.adjust($color-purple-text, $lightness: -10%);
      border-color: color.adjust($color-purple-text, $lightness: -10%);
      transform: translateY(0);
      box-shadow: 0 1px 2px 0 rgba(67, 73, 221, 0.2);
    }
  }

  &.el-button--danger {
    &.is-plain {
      background-color: white;
      border-color: #f56c6c;
      color: #f56c6c;

      &:hover {
        background-color: #fef0f0;
        border-color: #f56c6c;
        color: #f56c6c;
        box-shadow: 0 2px 4px 0 rgba(245, 108, 108, 0.2);
        transform: translateY(-1px);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }

  &.el-button--default {
    background-color: white;
    border-color: $gray-200;
    color: $gray-700;

    &:hover {
      background-color: $gray-50;
      border-color: $gray-300;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  }

  &.el-button--small {
    padding: 0 12px;
    font-size: 0.8125rem;
    height: 32px;
  }
}

// Image Upload Styling
.img-uploader {
  border: 2px dashed $gray-300;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
  background-color: $gray-50;
  min-height: 200px;
  height: 200px;

  &:hover {
    border-color: $color-purple-text;
    background-color: color.adjust($color-purple-text, $lightness: 45%);
  }

  .uploaded-image {
    width: 100%;
    height: 200px;
    object-fit: cover;
    display: block;
  }

  .upload-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
    height: 100%;
  }

  .img-uploader-icon {
    width: 48px;
    height: 48px;
    margin-bottom: 0.75rem;
    display: block;
    opacity: 0.5;
  }

  .upload-picture-text {
    text-align: center;
    color: $gray-600;
    font-size: 0.625rem !important;
    margin-bottom: 0.5rem;
    font-weight: 400;
    line-height: 1.2;
  }

  .fake-upload-button {
    pointer-events: none;
    height: 36px !important;
    padding: 0 20px !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    border-radius: 8px !important;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;
    border-width: 1px !important;
  }

  .pin-bx {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
    
    .el-button {
      height: 36px;
      padding: 0 20px;
      font-size: 0.875rem;
      font-weight: 600;
      border-radius: 8px;
    }
  }
}

// Additional specificity for upload text
:deep(.img-uploader) {
  .upload-picture-text {
    font-size: 0.625rem !important;
  }
}

// Page Background
:deep(.app-main-content-layout) {
  background-color: $gray-50;
}

// Title Styling
h5 {
  font-size: 1rem;
  font-weight: 600;
  color: $gray-900;
}

// Responsive Design
@media (max-width: 768px) {
  .c-segment-detail-form {
    padding: 1.5rem;
  }

  .c-content-section {
    padding: 1.25rem;
    border-radius: 12px;
  }

  :deep(.grid-2-cols) {
    gap: 1rem;
  }

  :deep(.el-form-item) {
    margin-bottom: 1.25rem;

    .el-form-item__label {
      font-size: 0.8125rem;
    }

    .el-input__inner,
    .el-textarea__inner {
      font-size: 0.8125rem;
    }
  }

  :deep(.el-button) {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }
}

@media (max-width: 576px) {
  .c-segment-detail-form {
    padding: 1rem;
  }

  .c-content-section {
    padding: 1rem;
    border-radius: 12px;
  }
}
</style>

<script lang="ts">
import {
  defineComponent,
  reactive,
  ref,
  onMounted,
} from 'vue';
import { requiredRule } from '@/data/rules';
import fileUpload from '@/shared/upload';
import shared from './shared';
import { resizeAndConvertToPNG } from '@/shared/utility-functions';

export default defineComponent({
  name: 'ManageContent_Directory_SegmentDetail',
  setup() {
    const {
      route,
      router,
      loading,
      getSegment,
      saveSegment,
      removeItemGetConfirm,
    } = shared();
    const refForm = ref<any>(null);
    const { id } = route.params;
    const { uploadStatus, beforeUpload, handleUploadRequest } = fileUpload('segments', 'Segment');
    const item = reactive({
      id: -1,
      cover: null,
      segment: '',
      description: '',
      entity: null,
      entityId: null,
      isActive: true,
    });

    const uploading = ref(false);

    async function getItem() {
      loading.value = true;
      const itm = await getSegment(id);
      item.id = itm.id;
      item.cover = itm.cover;
      item.segment = itm.segment;
      item.description = itm.description;
      item.entity = itm.entity;
      item.entityId = itm.entityId;
      item.isActive = itm.isActive;
      loading.value = false;
    }

    async function saveItem() {
      await saveSegment({
        id: item.id,
        cover: item.cover,
        segment: item.segment,
        description: item.description,
        entity: item.entity,
        entityId: item.entityId,
        isActive: item.isActive,
      });
    }

    async function handleSegmentCoverUpload(payload) {
      try {
        uploading.value = true;
        
        if (payload.file && payload.file.type.startsWith('image/')) {
          const processedImage = await resizeAndConvertToPNG(payload.file, 1200, 800, 0.9);
          payload.file = processedImage;
        }
        
        await handleUploadRequest(payload);
        uploading.value = false;
        if (uploadStatus.status === 'ok') {
          item.cover = uploadStatus.resourceUrl;
        }
      } catch (error) {
        console.error('Error processing or uploading cover image:', error);
        uploading.value = false;
      }
    }

    function validateForm() {
      refForm.value.validate(async (valid) => {
        if (valid) await saveItem();
        return false;
      });
    }

    onMounted(async () => {
      await getItem();
    });

    async function removeItem() {
      await saveSegment({
        id: item.id,
        cover: item.cover,
        segment: item.segment,
        description: item.description,
        entity: item.entity,
        entityId: item.entityId,
        isActive: false,
      });
      router.push('/manage-content/directory/segments');
    }

    function removeSegment() {
      removeItemGetConfirm(removeItem);
    }

    return {
      // data
      refForm,
      rules: {
        cover: requiredRule('cover'),
        segment: requiredRule('segment'),
        description: requiredRule('description'),
      },
      item,
      loading,
      uploading,

      // methods
      validateForm,
      removeSegment,
      handleSegmentCoverUpload,
      beforeUpload,
      handleSuccess: () => console.log('success'),
      handleError: () => console.log('error'),
    };
  },
});
</script>
